import { createSlice } from '@reduxjs/toolkit';

import endPoints from '../api/endpoint';
import {
  deleteApiCall,
  getApiCall,
  postApiCall,
  putApiCall,
} from '../api/methods';
import { locationState } from '../types';
import { notify } from '../Utils/toastify';

import { setCommonDataLoader } from './commonSlice';
import { setOrderSetCount } from './ProductionSlice';
import { setSpendings } from './dashboardSlice';
import store from './store';

const initialState: locationState = {
  setDetails: {},
  departmentList: {},
  setList: {},
  locationSpendingGraph: {},
  setLocations: {},
  setLocationsdetails: {},
  setlistLocations: {},
  setEpisodes: {},
  loading: false,
  settinglist: {},
  memberdetails: {},
  setlistAddresses: {},
  activeSets: {},
  setlocationBanner: {},
  locationFormData: {},
  contacts: [],
};

// Note: this slice's `name` must stay unique across the store — it previously
// collided with setSlice.ts (both were 'set'), which caused their actions to
// silently mutate each other's state. See slice `name: 'location'` below.
const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setLocationDetails: (state: any, action: any) => {
      const { payload }: any = action;
      state.setLocationsdetails = payload;
    },
    updateSetDepartmentlist: (state: any, action: any) => {
      const { payload }: any = action;
      state.departmentList = payload;
    },
    updateSetList: (state: any, action: any) => {
      const { payload }: any = action;
      state.setList = payload;
    },
    setLocationSpendingGraph: (state, action) => {
      const { payload }: any = action;
      state.locationSpendingGraph = payload;
    },
    setSetsLocationList: (state, action) => {
      const { payload }: any = action;
      state.setLocations = payload;
    },
    setMemberDetails: (state, action) => {
      const { payload }: any = action;
      state.memberdetails = payload;
    },
    setSetsEpisodeList: (state, action) => {
      const { payload }: any = action;
      state.setEpisodes = payload;
    },
    setLocationDetailsBanner: (state, action) => {
      const { payload }: any = action;
      state.setlocationBanner = payload;
    },
    setlistbyEpisodeandProductionId: (state, action) => {
      const { payload }: any = action;
      state.settinglist = payload;
    },
    setSetlistlocation: (state, action) => {
      const { payload }: any = action;
      state.setlistLocations = payload;
    },
    setSetlistAddress: (state, action) => {
      const { payload }: any = action;
      state.setlistAddresses = payload;
    },
    setLoading: (state, action) => {
      const { payload }: any = action;
      state.loading = payload;
    },
    setActiveSet: (state, action) => {
      const { payload }: any = action;
      state.activeSets = payload;
    },
    setLocationFormDetails: (state, action) => {
      const { payload }: any = action;
      state.locationFormData = payload;
    },
    setLocationContactsList: (state, action) => {
      const { payload }: any = action;
      state.contacts = payload;
    },
  },
});

export const {
  setLocationDetails,
  updateSetDepartmentlist,
  updateSetList,
  setLocationSpendingGraph,
  setSetsLocationList,
  setSetlistlocation,
  setLoading,
  setMemberDetails,
  setlistbyEpisodeandProductionId,
  setSetsEpisodeList,
  setLocationDetailsBanner,
  setSetlistAddress,
  setActiveSet,
  setLocationFormDetails,
  setLocationContactsList,
} = locationSlice.actions;

export default locationSlice.reducer;

export const getLocationDetails = (locationId: any, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true)); // Show loader before the API call

  getApiCall(
    endPoints.location.locationDetailList + locationId,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setLocationDetails(data.data));
        if (cb) cb(data.data); // Call the callback function if provided
      }
      dispatch(setLoading(false)); // Hide loader after successful response
    },
    (e: any) => {
      console.error('Error fetching production details:', e);
      dispatch(setCommonDataLoader(false));
    },
  );
};
export const getDepartmentListOfSet = (setId: any, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));
  dispatch(setCommonDataLoader(true));
  const apiendpoint = `${endPoints.set.departmentList}&entityId=${setId}`;
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(updateSetDepartmentlist(data.data));
        dispatch(setCommonDataLoader(false));
        cb(data.data);
      }
      dispatch(setLoading(false));
    },

    () => {
      dispatch(setCommonDataLoader(false));
      dispatch(setLoading(false)); // Hide loader after error
    },
  );
};

export const getSetListlocation = (setId: any, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true)); // Show loader before the API call
  dispatch(setCommonDataLoader(true));
  const apiendpoint = `${endPoints.location.locationsetslist}?locationId=${setId}`;
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setSetlistlocation(data.data));
        dispatch(setCommonDataLoader(false));
        cb(data.data);
      }
      dispatch(setLoading(false));
    },

    () => {
      dispatch(setCommonDataLoader(false));
      dispatch(setLoading(false)); // Hide loader after error
    },
  );
};

export const getSetAddresslocation = (setId: any, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true)); // Show loader before the API call
  dispatch(setCommonDataLoader(true));
  const apiendpoint = `${endPoints.getMultipleSetsLocationData}?locationId=${setId}`;
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data.data) {
        dispatch(setSetlistAddress(data.data));
        dispatch(setCommonDataLoader(false));
        cb(data.data);
      }
      dispatch(setLoading(false));
    },

    () => {
      dispatch(setCommonDataLoader(false));
      dispatch(setLoading(false)); // Hide loader after error
    },
  );
};

export const getLocationSpendingGraph = (
  type: string,
  id: string,
  range: string,
  productionId?: string,
  cb?: any,
) => {
  const { dispatch } = store;

  // Build the API endpoint with the query and studioId
  let apiendpoint = endPoints.production.spendingGraph(type, id, range);

  if (productionId) {
    apiendpoint = `${apiendpoint}&productionId=${productionId}`;
  }

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setLocationSpendingGraph(data?.data));

        cb(data.data);
      }
    },
    (e: any) => {
      notify(e?.data?.message, 'error');
    },
  );
};

export const getSetLocationList = (setId: string, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  const apiendpoint = `${endPoints.set.setLocation}?setId=${setId}`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setSetsLocationList(data?.data));

        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setLoading(false));
    },
    () => {
      // Handle error, stop loading
      dispatch(setLoading(false));
    },
  );
};

export const createLocation = (values: any, cb?: any, errCb?: any) => {
  postApiCall(
    endPoints.location.createLocation,
    values,
    (s: any) => {
      const {
        data: { statusCode, message, data },
      } = s;
      if (statusCode && statusCode === 200) {
        notify(message, 'success');
        if (cb) cb(data);
      }
    },
    (e: any) => {
      if (errCb) errCb();
      if (e?.data && e?.data.message) {
        notify(e?.data.message, 'error');
      }
    },
  );
};
export const getEpisodeListbyProductionId = (
  productionId: string,
  cb?: any,
) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  const apiendpoint = `${endPoints.location.episodelist}?productionId=${productionId}`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setSetsEpisodeList(data?.data));

        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setLoading(false));
    },
    () => {
      // Handle error, stop loading
      dispatch(setLoading(false));
    },
  );
};
export const getLocationDetailsBanner = (locationId: any, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  const apiendpoint = endPoints.locationDetailsbanner + locationId;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setLocationDetails(data?.data));

        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setLoading(false));
    },
    (e: any) => {
      // Handle error, stop loading
      notify(e?.data?.messgae, 'error');
      dispatch(setLoading(false));
    },
  );
};

export const getSetlistbyEpisodeandProductionId = (
  productionId: string,
  episodeId?: string,
  cb?: any,
) => {
  const { dispatch } = store;

  // Build the API endpoint conditionally based on provided parameters
  let apiendpoint = endPoints.location.setlist;

  const params = [];

  if (productionId) {
    params.push(`productionId=${productionId}`);
  }

  if (episodeId) {
    params.push(`episodeId=${episodeId}`);
  }

  // Append parameters to the endpoint
  if (params.length > 0) {
    apiendpoint += `?${params.join('&')}`;
  }

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setlistbyEpisodeandProductionId(data?.data));
        cb(data.data);
      }
    },
    () => {
      // Handle error, stop loading
    },
  );
};

export const updateLocation = (
  locationId: string,
  values: any,
  cb?: any,
  errCb?: any,
) => {
  putApiCall(
    `${endPoints.location.cretaelocation}/${locationId}`,
    values,
    (s: any) => {
      const {
        data: { statusCode, message },
      } = s;

      if (statusCode && statusCode === 200) {
        notify(message, 'success');
        if (cb) cb();
      }
    },
    (e: any) => {
      if (errCb) errCb();
      if (e?.data && e?.data.message) {
        notify(e?.data.message, 'error');
      }
    },
  );
};

export const UpdateMember = (
  memberId: string,
  values: any,
  cb?: any,
  cbe?: any,
  isStudio?: boolean,
) => {
  putApiCall(
    `${endPoints.updateCrewMember}${memberId}`,
    values,
    (s: any) => {
      const {
        data: { statusCode, message },
      } = s;

      if (statusCode && statusCode === 200) {
        if (!isStudio) {
          notify(message, 'success');
        }

        if (cb) cb();
      }
    },
    (e: any) => {
      if (e?.data && e?.data.message) {
        notify(e?.data.message, 'error');
        if (cbe) cbe();
      }
    },
  );
};

export const getMemberDetails = (memberId: string, cb?: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  const apiendpoint = `${endPoints.updateCrewMember}${memberId}?memberType=CONTACT`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setMemberDetails(data?.data));

        cb(data.data);
      }

      // Stop loading indicator
      dispatch(setLoading(false));
    },
    () => {
      // Handle error, stop loading
      dispatch(setLoading(false));
    },
  );
};

export const deleteMember = (
  memberId: string | any,
  memberType: any,
  cb?: any,
  isStudio?: boolean,
) => {
  const { dispatch } = store;
  dispatch(setLoading(true));
  deleteApiCall(
    `${endPoints.updateCrewMember}${memberId}?memberType=${memberType}`,
    {},
    (s: any) => {
      const {
        data: { statusCode, message },
      } = s;
      if (statusCode && statusCode === 200) {
        if (!isStudio) {
          notify(message, 'success');
        }

        if (cb) cb();
        dispatch(setLoading(false));
      }
    },
    (e: any) => {
      if (e?.data && e?.data.message) {
        notify(e?.data.message, 'error');
        dispatch(setLoading(false));
      }
    },
  );
};

export const getSetbyLocationId = (locationId: string, cb?: any) => {
  const { dispatch } = store;

  const apiendpoint = `${endPoints.location.setLocationList}?locationId=${locationId}`;

  // Make the API call
  getApiCall(
    apiendpoint,
    (s: any) => {
      const { data } = s;
      if (data?.data) {
        dispatch(setActiveSet(data?.data));

        cb(data.data);
      }
    },
    () => {
      // Handle error, stop loading
    },
  );
};

// ------ ----- ------- API PARALLEL CALLING METHODS ----------

export const getLocationDetailsBannerAPI = (locationId: any) => {
  const { dispatch } = store;
  dispatch(setLoading(true));

  return new Promise((resolve, reject) => {
    const apiendpoint = endPoints.locationDetailsbanner + locationId;

    getApiCall(
      apiendpoint,
      (s: any) => {
        const { data } = s;
        if (data?.data) {
          dispatch(setLocationDetails(data.data));
          resolve(data.data);
        } else {
          reject('No data');
        }
        dispatch(setLoading(false));
      },
      (e: any) => {
        notify(e?.data?.message, 'error');
        dispatch(setLoading(false));
        reject(e);
      },
    );
  });
};

export const getEntitySpendings = (type?: any, id?: string) => {
  const { dispatch } = store;
  dispatch(setCommonDataLoader(true));

  return new Promise((resolve, reject) => {
    let endPoint = endPoints.dashBoard.spendings;
    endPoint += `?entityType=${type}&entityId=${id}`;

    getApiCall(
      endPoint,
      (s: any) => {
        const { data } = s;
        if (data?.data) {
          dispatch(setSpendings(data.data));
          resolve(data.data);
        } else {
          reject('No data');
        }
        dispatch(setCommonDataLoader(false));
      },
      (e: any) => {
        notify(e?.data?.message, 'error');
        dispatch(setCommonDataLoader(false));
        reject(e);
      },
    );
  });
};

export const getLocationOrderCountAPI = (entityId: any, type?: string) => {
  const { dispatch } = store;

  return new Promise((resolve, reject) => {
    let apiendpoint = endPoints.production.setordercount;

    if (type === 'episode') {
      apiendpoint += `?episodeId=${entityId}`;
    } else if (type === 'location') {
      apiendpoint += `?locationId=${entityId}`;
    } else if (type === 'set') {
      apiendpoint += `?setId=${entityId}`;
    } else {
      apiendpoint += `?productionId=${entityId}`;
    }

    getApiCall(
      apiendpoint,
      (s: any) => {
        const { data } = s;
        if (data?.data) {
          dispatch(setOrderSetCount(data.data));
          resolve(data.data);
        } else {
          reject('No data');
        }
      },
      (e: any) => {
        reject(e);
      },
    );
  });
};

export const updateLocationFormDetails = (data: any, cb?: any) => {
  const { dispatch } = store;

  dispatch(setLocationFormDetails(data));
  if (cb) cb(data);
};

export const updateContactsList = (data: any) => {
  const { dispatch } = store;

  const contacts = store.getState().location.contacts;

  dispatch(setLocationContactsList([...contacts, data]));
};

export const removeContactsList = (data: any) => {
  const { dispatch } = store;

  dispatch(setLocationContactsList(data));
};

export const removeLocationSpendingGraph = () => {
  const { dispatch } = store;
  dispatch(setLocationSpendingGraph({}));
};
